// Onboarding del comercial — wizard que se ejecuta en su primer login.
// 6 pasos (lote-90): Bienvenida → Plan → Cobros → Funcionamiento → Objetivo →
// Personaliza (foto: upload o Slack).
//
// Si el user salta o termina, se guarda `onboardingCompletedAt` en el perfil
// para que no vuelva a salir. La simulación desde Configuración no persiste
// nada (manager pasa onSkip/onComplete que no graban).
import { useState, useRef, MONTHS, fmt } from './common.jsx';

export function OnboardingModal({user, cats, onComplete, onSkip}) {
  const [step, setStep] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl || '');
  const [saving, setSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // { type:'ok'|'err', msg }
  const fileInputRef = useRef(null);

  const plan = (cats||[]).find(c => c.id === user.defaultCategoryId);
  const now = new Date();
  const monthKey = now.getFullYear() + '_' + (now.getMonth()+1);
  const objective = parseFloat(user.monthlyObjectives?.[monthKey]) || 0;
  const monthName = MONTHS[now.getMonth()];

  // Ejemplo numérico que usamos en el paso del plan: si vendes 1.000€ con el
  // % de 3 cuotas, ¿cuánto cobras? Sacamos el % real del plan del user.
  const planPctFor = (k) => parseFloat(plan?.paymentInstallments?.[k]) || 0;
  const examplePct = planPctFor('3') || planPctFor('6') || planPctFor('1') || 0;
  const exampleK = planPctFor('3') > 0 ? '3' : planPctFor('6') > 0 ? '6' : '1';

  // ── Subir foto desde archivo ──
  // 1. file → FileReader.readAsDataURL → img.src
  // 2. dibujamos a canvas 256x256 (cover) → toDataURL('image/jpeg', .85)
  // 3. POST /api/users/:id/avatar { dataUrl }
  // El resize agresivo nos deja avatares <50KB sin perder reconocibilidad.
  const uploadFromFile = (file) => {
    setUploadStatus(null);
    if (!file || !file.type.startsWith('image/')) {
      setUploadStatus({ type:'err', msg:'Selecciona una imagen (jpg, png, webp)' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = async () => {
        const SIZE = 256;
        const canvas = document.createElement('canvas');
        canvas.width = SIZE; canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        // Cover: escalamos la imagen para que rellene 256x256 cropeando lo
        // que sobre (manteniendo aspect ratio). Si vertical, centro vertical.
        const scale = Math.max(SIZE / img.width, SIZE / img.height);
        const w = img.width * scale, h = img.height * scale;
        const x = (SIZE - w) / 2, y = (SIZE - h) / 2;
        ctx.drawImage(img, x, y, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        try {
          const r = await fetch('/api/users/' + user.id + '/avatar', {
            method:'POST', headers:{'Content-Type':'application/json'},
            body: JSON.stringify({ dataUrl })
          });
          const data = await r.json();
          if (!r.ok) {
            setUploadStatus({ type:'err', msg: data.error || 'Error subiendo' });
            return;
          }
          setAvatarUrl(data.avatarUrl);
          setUploadStatus({ type:'ok', msg:'¡Foto subida!' });
        } catch (err) {
          setUploadStatus({ type:'err', msg:'No pudimos subir la foto: ' + (err?.message || err) });
        }
      };
      img.onerror = () => setUploadStatus({ type:'err', msg:'No se pudo leer la imagen' });
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  // ── Importar foto desde Slack ──
  // Llama al endpoint backend que hace lookup-by-email + descarga la imagen.
  // Si la env var SLACK_BOT_TOKEN no está, el endpoint devuelve 503 con un
  // mensaje útil. Lo mostramos sin romper la pantalla.
  const importFromSlack = async () => {
    setUploadStatus(null);
    if (!user.email) {
      setUploadStatus({ type:'err', msg:'Necesitas un email en tu perfil para importar de Slack' });
      return;
    }
    setSaving(true);
    try {
      const r = await fetch('/api/users/' + user.id + '/avatar/from-slack', { method:'POST' });
      const data = await r.json();
      if (!r.ok) {
        setUploadStatus({ type:'err', msg: data.error || 'No se pudo importar', help: data.help });
        return;
      }
      setAvatarUrl(data.avatarUrl);
      setUploadStatus({ type:'ok', msg: `¡Importado de Slack como "${data.displayName || user.name}"!` });
    } catch (err) {
      setUploadStatus({ type:'err', msg:'Error contactando al servidor: ' + (err?.message || err) });
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await fetch('/api/users/' + user.id, {
        method:'PUT',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          onboardingCompletedAt: new Date().toISOString(),
          avatarUrl: avatarUrl || null,
          _actor: { id: user.id, name: user.name }
        })
      });
      onComplete({ avatarUrl });
    } catch {
      onComplete({ avatarUrl });
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    try {
      await fetch('/api/users/' + user.id, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ onboardingCompletedAt: new Date().toISOString() })
      });
    } catch {}
    onSkip();
  };

  const Dot = ({i}) => (
    <div style={{
      width: i===step?24:8, height:8, borderRadius:4,
      background: i===step?'var(--pu)':'var(--bds)',
      transition:'all .25s'
    }}/>
  );

  // ── Step 0: bienvenida ──
  const stepWelcome = (
    <>
      <div style={{fontSize:60,textAlign:'center',marginBottom:8}}>👋</div>
      <h2 style={{textAlign:'center',margin:'0 0 10px',fontSize:22}}>¡Bienvenido, {user.name?.split(' ')[0]}!</h2>
      <p style={{textAlign:'center',color:'var(--tx2)',lineHeight:1.6,maxWidth:420,margin:'0 auto 8px'}}>
        Esta es tu app de comisiones de Ucademy. Aquí verás <strong>cuánto ganas</strong> mes a mes y por qué.
      </p>
      <p style={{textAlign:'center',color:'var(--tx-mu)',fontSize:12,maxWidth:380,margin:'0 auto'}}>
        Te explicamos lo importante en menos de 1 minuto.
      </p>
    </>
  );

  // ── Step 1: Tu plan (mejorado con ejemplo de cálculo) ──
  const stepPlan = (
    <>
      <div style={{fontSize:48,textAlign:'center',marginBottom:6}}>📋</div>
      <h2 style={{textAlign:'center',margin:'0 0 8px',fontSize:20}}>Tu plan de comisiones</h2>
      <p style={{textAlign:'center',color:'var(--tx-mu)',fontSize:12,margin:'0 0 14px',lineHeight:1.5}}>
        El porcentaje que ganas depende de en cuántas cuotas pague el cliente.
      </p>
      {plan ? (
        <>
          <div style={{background:'var(--pu-bg)',border:'1px solid var(--pu)',borderRadius:10,padding:'14px 16px',marginBottom:12}}>
            <div style={{fontSize:14,fontWeight:700,color:'var(--pu-d)',marginBottom:8}}>{plan.name}</div>
            <div style={{fontSize:11,color:'var(--tx-mu)',marginBottom:6,textTransform:'uppercase',letterSpacing:'.4px',fontWeight:600}}>% que ganas por cuotas</div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {Object.entries(plan.paymentInstallments||{}).filter(([,v]) => v > 0).map(([k,v]) => (
                <div key={k} style={{background:'var(--wh)',border:'1px solid var(--bds)',borderRadius:8,padding:'6px 10px',textAlign:'center',minWidth:50}}>
                  <div style={{fontSize:11,fontWeight:600,color:'var(--tx-mu)'}}>{k}x</div>
                  <div style={{fontSize:14,fontWeight:700,color:'var(--gr-d)'}}>{v}%</div>
                </div>
              ))}
            </div>
          </div>
          {examplePct > 0 && (
            <div style={{background:'var(--bg)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--tx2)',lineHeight:1.6,border:'1px dashed var(--bds)'}}>
              <strong style={{color:'var(--tx)'}}>Ejemplo:</strong> si cierras una venta de <strong>1.000€</strong> a <strong>{exampleK} cuota{exampleK!=='1'?'s':''}</strong>, ganas <strong style={{color:'var(--gr-d)'}}>{(1000*examplePct/100).toFixed(0)}€</strong> de comisión ({examplePct}% sobre 1.000€).
            </div>
          )}
        </>
      ) : (
        <p style={{textAlign:'center',color:'var(--tx-mu)',marginTop:12,fontSize:13}}>
          Tu manager aún no te ha asignado un plan. Pídele que lo configure desde Equipo → tu perfil.
        </p>
      )}
      <p style={{fontSize:11,color:'var(--tx-mu)',textAlign:'center',marginTop:12,lineHeight:1.5}}>
        Si tu plan cambia, lo verás reflejado en <strong>Mi perfil → Histórico mensual</strong>.
      </p>
    </>
  );

  // ── Step 2: Cobros (cómo se cobra realmente — NUEVO) ──
  const stepCobros = (
    <>
      <div style={{fontSize:48,textAlign:'center',marginBottom:6}}>💰</div>
      <h2 style={{textAlign:'center',margin:'0 0 8px',fontSize:20}}>Cuándo cobras</h2>
      <p style={{textAlign:'center',color:'var(--tx-mu)',fontSize:12,margin:'0 0 14px',lineHeight:1.5,maxWidth:420,marginLeft:'auto',marginRight:'auto'}}>
        La comisión se calcula sobre la <strong>facturación real cobrada</strong>, no sobre lo firmado.
      </p>
      <div style={{display:'grid',gap:10,marginBottom:14}}>
        <div style={{display:'flex',gap:12,alignItems:'flex-start',padding:'10px 14px',background:'var(--bg)',borderRadius:8,border:'1px solid var(--bds)'}}>
          <div style={{fontSize:22,lineHeight:1}}>1️⃣</div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:'var(--tx)'}}>Cierras la venta</div>
            <div style={{fontSize:12,color:'var(--tx2)',lineHeight:1.5}}>Se contabiliza en el mes en que el deal pasa a "Closed Won" en HubSpot.</div>
          </div>
        </div>
        <div style={{display:'flex',gap:12,alignItems:'flex-start',padding:'10px 14px',background:'var(--bg)',borderRadius:8,border:'1px solid var(--bds)'}}>
          <div style={{fontSize:22,lineHeight:1}}>2️⃣</div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:'var(--tx)'}}>El cliente paga sus cuotas</div>
            <div style={{fontSize:12,color:'var(--tx2)',lineHeight:1.5}}>Cada cuota cobrada suma a tu comisión. La 1ª cuota se considera pagada al cerrar.</div>
          </div>
        </div>
        <div style={{display:'flex',gap:12,alignItems:'flex-start',padding:'10px 14px',background:'var(--bg)',borderRadius:8,border:'1px solid var(--bds)'}}>
          <div style={{fontSize:22,lineHeight:1}}>3️⃣</div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:'var(--tx)'}}>Si hay impago o baja</div>
            <div style={{fontSize:12,color:'var(--tx2)',lineHeight:1.5}}>Se descuenta de tu facturación bruta (en <strong>Cobros</strong> verás "Facturación neta"). Tu tramo conseguido <strong>se mantiene</strong>.</div>
          </div>
        </div>
      </div>
      <p style={{fontSize:11,color:'var(--tx-mu)',textAlign:'center',marginTop:8,lineHeight:1.5}}>
        Ve a <strong>Cobros</strong> y pulsa el nombre del mes para ver el desglose paso a paso.
      </p>
    </>
  );

  // ── Step 3: Funcionamiento general (NUEVO) ──
  const stepHow = (
    <>
      <div style={{fontSize:48,textAlign:'center',marginBottom:6}}>🧭</div>
      <h2 style={{textAlign:'center',margin:'0 0 8px',fontSize:20}}>Cómo te muevas por la app</h2>
      <p style={{textAlign:'center',color:'var(--tx-mu)',fontSize:12,margin:'0 0 14px',lineHeight:1.5,maxWidth:420,marginLeft:'auto',marginRight:'auto'}}>
        Hay 4 secciones que vas a usar a diario.
      </p>
      <div style={{display:'grid',gap:8}}>
        {[
          { icon:'🏠', name:'Inicio', desc:'Tus KPIs del mes (facturación, comisión, progreso vs objetivo) y los récords del equipo.' },
          { icon:'$', name:'Cobros', desc:'Cuánto vas a cobrar este mes. Pulsa el nombre de un mes para ver el cálculo detallado.' },
          { icon:'📈', name:'Mi Carrera', desc:'Insignias que has desbloqueado, gráficos de tu evolución y comparativas con el equipo.' },
          { icon:'📋', name:'Mi historial', desc:'Mes a mes lo que has facturado, comisionado, tu plan, tu equipo y tu rol en cada momento.' },
        ].map((s,i) => (
          <div key={i} style={{display:'flex',gap:12,alignItems:'flex-start',padding:'10px 14px',background:'var(--bg)',borderRadius:8,border:'1px solid var(--bds)'}}>
            <div style={{fontSize:18,width:24,textAlign:'center',flexShrink:0,fontWeight:700,color:'var(--tx2)'}}>{s.icon}</div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:'var(--tx)'}}>{s.name}</div>
              <div style={{fontSize:12,color:'var(--tx2)',lineHeight:1.5}}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <p style={{fontSize:11,color:'var(--tx-mu)',textAlign:'center',marginTop:12,lineHeight:1.5}}>
        Atajos: <kbd style={{background:'var(--bg)',padding:'1px 5px',borderRadius:3,fontSize:10,border:'1px solid var(--bds)'}}>⌘K</kbd> abre el buscador. El botón <strong>"? Tour de la sección"</strong> del sidebar relanza esta o cualquier guía.
      </p>
    </>
  );

  // ── Step 4: Tu objetivo ──
  const stepObjective = (
    <>
      <div style={{fontSize:48,textAlign:'center',marginBottom:6}}>🎯</div>
      <h2 style={{textAlign:'center',margin:'0 0 10px',fontSize:20}}>Tu objetivo de {monthName}</h2>
      {objective > 0 ? (
        <div style={{background:'var(--bg)',borderRadius:10,padding:'18px 16px',marginTop:14,textAlign:'center'}}>
          <div style={{fontSize:11,color:'var(--tx-mu)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'.4px'}}>Objetivo del mes</div>
          <div style={{fontSize:28,fontWeight:700,color:'var(--tx)',letterSpacing:'-.5px'}}>{fmt(objective)}</div>
          <div style={{fontSize:12,color:'var(--tx-mu)',marginTop:6}}>Lo verás en tu Inicio con barra de progreso y proyección.</div>
        </div>
      ) : (
        <p style={{textAlign:'center',color:'var(--tx-mu)',marginTop:12,fontSize:13,lineHeight:1.6}}>
          Aún no tienes objetivo configurado este mes. Pídele a tu manager que te ponga uno — ayuda a ver el progreso día a día.
        </p>
      )}
      <p style={{fontSize:11,color:'var(--tx-mu)',textAlign:'center',marginTop:14,lineHeight:1.5}}>
        Tu objetivo es en <strong>facturación</strong> (€ que cierras), no en comisión.
      </p>
    </>
  );

  // ── Step 5: Personaliza (foto con upload + Slack) ──
  const stepProfile = (
    <>
      <div style={{fontSize:48,textAlign:'center',marginBottom:6}}>📷</div>
      <h2 style={{textAlign:'center',margin:'0 0 8px',fontSize:20}}>Tu foto de perfil</h2>
      <p style={{textAlign:'center',color:'var(--tx2)',fontSize:13,margin:'0 0 16px',lineHeight:1.5}}>
        Ayuda a tus compañeros a identificarte en rankings y tablas. Opcional.
      </p>
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:18,marginBottom:16}}>
        {avatarUrl
          ? <img src={avatarUrl} alt="" style={{width:80,height:80,borderRadius:'50%',objectFit:'cover',border:'2px solid var(--bds)',background:'var(--bg)',flexShrink:0}} onError={e=>{e.target.style.display='none';}}/>
          : <div className="uav com" style={{width:80,height:80,fontSize:30,flexShrink:0}}>{(user.name?.[0]||'?').toUpperCase()}</div>}
        <div style={{display:'flex',flexDirection:'column',gap:8,flex:1,maxWidth:240}}>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{display:'none'}}
            onChange={e => { const f = e.target.files?.[0]; if (f) uploadFromFile(f); e.target.value=''; }}/>
          <button className="btn btn-pu btn-sm" onClick={() => fileInputRef.current?.click()} disabled={saving}>
            📁 Subir desde mi ordenador
          </button>
          <button className="btn btn-gh btn-sm" onClick={importFromSlack} disabled={saving || !user.email}
            title={user.email ? `Busca tu perfil en Slack por email (${user.email})` : 'Sin email — pide a tu manager que añada tu email'}>
            {saving ? '⏳ Buscando…' : '💬 Importar de Slack'}
          </button>
        </div>
      </div>
      {uploadStatus && (
        <div style={{
          fontSize:12,padding:'8px 12px',borderRadius:6,lineHeight:1.5,
          background: uploadStatus.type==='ok' ? 'rgba(10,187,135,.10)' : 'rgba(223,32,32,.08)',
          color: uploadStatus.type==='ok' ? 'var(--gr-d)' : 'var(--rd)',
          border: `1px solid ${uploadStatus.type==='ok' ? 'rgba(10,187,135,.3)' : 'rgba(223,32,32,.25)'}`
        }}>
          <div style={{fontWeight:600}}>{uploadStatus.msg}</div>
          {uploadStatus.help && <div style={{fontSize:11,color:'var(--tx-mu)',marginTop:3,fontStyle:'italic'}}>{uploadStatus.help}</div>}
        </div>
      )}
      <p style={{fontSize:11,color:'var(--tx-mu)',textAlign:'center',marginTop:12,lineHeight:1.5}}>
        Puedes cambiarla más adelante en <strong>Mi perfil</strong>.
      </p>
    </>
  );

  const steps = [stepWelcome, stepPlan, stepCobros, stepHow, stepObjective, stepProfile];
  const isLast = step === steps.length - 1;

  return (
    <div className="ov" style={{zIndex:1000}}>
      <div className="modal ani" style={{maxWidth:560,padding:0}}>
        <div style={{padding:'30px 32px 18px',minHeight:360}}>
          {steps[step]}
        </div>
        <div style={{padding:'14px 22px 18px',borderTop:'1px solid var(--bds)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap'}}>
          <div style={{display:'flex',gap:5,alignItems:'center'}}>
            {steps.map((_,i)=><Dot key={i} i={i}/>)}
          </div>
          <div style={{display:'flex',gap:8}}>
            {!isLast && <button className="btn btn-gh btn-sm" onClick={skip} disabled={saving}>Saltar</button>}
            {step > 0 && <button className="btn btn-gh btn-sm" onClick={()=>setStep(s=>s-1)} disabled={saving}>Atrás</button>}
            {!isLast
              ? <button className="btn btn-pu btn-sm" onClick={()=>setStep(s=>s+1)}>Siguiente →</button>
              : <button className="btn btn-pu btn-sm" onClick={finish} disabled={saving}>{saving?'Guardando…':'Empezar a usar la app ✓'}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
